import { APPROVALS_HELP_ARTICLE_URL } from '@easy-expense/data-firestore-client';
import { getTranslation } from '@easy-expense/intl-client';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { APPROVALS_TUTORIAL_VIDEO } from '@easy-expense/utils-shared';
import React from 'react';

import { Button } from '../Button.components';
import { Modal } from '../Shared/Modal.component';

const MODAL_WIDTH = 560;
const VIDEO_HEIGHT = MODAL_WIDTH * APPROVALS_TUTORIAL_VIDEO.aspectRatio;

type TutorialActions = {
  isAdmin: boolean;
  onEnable: () => Promise<boolean>;
  onSkip: () => void;
  onDone: () => void;
  onViewSettings: () => void;
};

/** The approvals tutorial video, at the video's own aspect ratio. */
const ApprovalsTutorialVideo: React.FC = () => {
  return (
    <iframe
      src={APPROVALS_TUTORIAL_VIDEO.url}
      title={getTranslation('Expense Approvals tutorial')}
      width={MODAL_WIDTH}
      height={VIDEO_HEIGHT}
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
      style={{ border: 'none', borderRadius: 8 }}
    />
  );
};

/**
 * Admins move on to the confirm step. Managers can't write workspace settings (firestore.rules),
 * so they're told who can.
 */
const VideoStepAction: React.FC<{ isAdmin: boolean; onContinue: () => void }> = ({
  isAdmin,
  onContinue,
}) => {
  if (!isAdmin) {
    return (
      <OpenSans.Secondary size="s-16" center>
        {getTranslation('Ask a workspace admin to turn on approvals.')}
      </OpenSans.Secondary>
    );
  }
  return (
    <Button.Primary
      content={getTranslation('Turn on approvals now')}
      variant="bold"
      center
      onClick={onContinue}
    />
  );
};

const ConfirmBullet: React.FC<{ text: string }> = ({ text }) => {
  return (
    <Layout.Row style={{ alignItems: 'flex-start' }}>
      <OpenSans.Primary size="s-16">•</OpenSans.Primary>
      <Spacer.Horizontal size={8} />
      <OpenSans.Primary size="s-16" grow>
        {text}
      </OpenSans.Primary>
    </Layout.Row>
  );
};

/**
 * The step before approvals turn on. It spells out that only new expenses are affected (the
 * `onExpenseWritten` trigger only stamps newly created expenses), links the help article with PDFs
 * for members, and makes enabling an explicit choice with a way out. While the write is saving the
 * buttons are inactive, since skipping wouldn't stop the write, and a rejected write is reported
 * here so the admin can retry.
 */
const ConfirmStep: React.FC<{
  isSaving: boolean;
  hasError: boolean;
  onEnable: () => void;
  onSkip: () => void;
}> = ({ isSaving, hasError, onEnable, onSkip }) => {
  let enableContent = getTranslation('Enable Expense Approvals');
  if (isSaving) {
    enableContent = getTranslation('Turning on approvals...');
  }

  return (
    <Layout.Column>
      <OpenSans.Primary size="xl-28" weight="bold-700">
        {getTranslation('Turn on expense approvals?')}
      </OpenSans.Primary>
      <Spacer.Vertical size={16} />
      <ConfirmBullet
        text={getTranslation("Only new expenses need approval. Existing expenses won't change.")}
      />
      <Spacer.Vertical size={8} />
      <ConfirmBullet
        text={getTranslation(
          'Expenses your members add will wait for a manager or admin to approve them.',
        )}
      />
      <Spacer.Vertical size={8} />
      <ConfirmBullet
        text={getTranslation('Expenses added by managers and admins are approved automatically.')}
      />
      <Spacer.Vertical size={8} />
      <ConfirmBullet
        text={getTranslation('You can turn this off any time in Workspace settings.')}
      />
      <Spacer.Vertical size={16} />
      <a
        href={APPROVALS_HELP_ARTICLE_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: theme.colors.brandPrimary }}
      >
        <OpenSans.Pressable size="s-16" weight="bold-700">
          {getTranslation('Read the approvals guide and download PDFs for your team')}
        </OpenSans.Pressable>
      </a>
      <Spacer.Vertical size={24} />
      {hasError && (
        <>
          <OpenSans.Primary size="s-16" color="destructive" center>
            {getTranslation("Couldn't turn on approvals. Please try again.")}
          </OpenSans.Primary>
          <Spacer.Vertical size={12} />
        </>
      )}
      <Button.Primary
        content={enableContent}
        variant="bold"
        center
        active={!isSaving}
        onClick={onEnable}
      />
      <Spacer.Vertical size={12} />
      <Button.Secondary
        content={getTranslation('Skip, Maybe Later')}
        variant="bold"
        active={!isSaving}
        center
        onClick={onSkip}
      />
    </Layout.Column>
  );
};

/**
 * Shown once Firestore has accepted the write, so "approvals are on" is never said early. Done
 * keeps the admin where they were; the settings link is there if they want to check the toggle.
 */
const DoneStep: React.FC<{ onDone: () => void; onViewSettings: () => void }> = ({
  onDone,
  onViewSettings,
}) => {
  return (
    <Layout.Column>
      <OpenSans.Primary size="xl-28" weight="bold-700">
        {'✓ ' + getTranslation('Expense approvals are on')}
      </OpenSans.Primary>
      <Spacer.Vertical size={16} />
      <OpenSans.Primary size="s-16">
        {getTranslation('New expenses from your members will now need approval.')}
      </OpenSans.Primary>
      <Spacer.Vertical size={24} />
      <Button.Primary content={getTranslation('Done')} variant="bold" center onClick={onDone} />
      <Spacer.Vertical size={12} />
      <Button.Secondary
        content={getTranslation('View workspace settings')}
        variant="bold"
        center
        onClick={onViewSettings}
      />
    </Layout.Column>
  );
};

/**
 * The modal's steps: video, confirm (admins only), then done once the write lands. Rendered inside
 * `Modal`, which unmounts its children when closed, so the modal always reopens on the video.
 */
const ApprovalsTutorialContent: React.FC<
  TutorialActions & { isSaving: boolean; setIsSaving: (isSaving: boolean) => void }
> = ({ isAdmin, onEnable, onSkip, onDone, onViewSettings, isSaving, setIsSaving }) => {
  const [step, setStep] = React.useState<'video' | 'confirm' | 'done'>('video');
  const [hasError, setHasError] = React.useState(false);

  async function onConfirmEnable() {
    setIsSaving(true);
    setHasError(false);
    const isEnabled = await onEnable();
    setIsSaving(false);
    if (isEnabled) {
      setStep('done');
    } else {
      setHasError(true);
    }
  }

  if (step === 'done') {
    return <DoneStep onDone={onDone} onViewSettings={onViewSettings} />;
  }
  if (step === 'confirm') {
    return (
      <ConfirmStep
        isSaving={isSaving}
        hasError={hasError}
        onEnable={onConfirmEnable}
        onSkip={onSkip}
      />
    );
  }
  return (
    <Layout.Column>
      <OpenSans.Primary size="xl-28" weight="bold-700">
        {getTranslation('New Feature: Expense Approvals')}
      </OpenSans.Primary>
      <Spacer.Vertical size={16} />
      <ApprovalsTutorialVideo />
      <Spacer.Vertical size={24} />
      <VideoStepAction isAdmin={isAdmin} onContinue={() => setStep('confirm')} />
    </Layout.Column>
  );
};

/**
 * Modal opened from the approvals banner: the tutorial video, then (for admins) a confirm step
 * before approvals turn on, and a done step once they are. Saving state lives here so a backdrop
 * click can't close the modal mid-write, since closing wouldn't stop the write.
 */
export const ApprovalsTutorialModal: React.FC<
  TutorialActions & { showModal: boolean; setShowModal: (showModal: boolean) => void }
> = ({ showModal, setShowModal, ...actions }) => {
  const [isSaving, setIsSaving] = React.useState(false);

  function onSetShowModal(nextShowModal: boolean) {
    if (isSaving) {
      return;
    }
    setShowModal(nextShowModal);
  }

  return (
    <Modal showModal={showModal} setShowModal={onSetShowModal}>
      <Layout.Column style={{ width: MODAL_WIDTH }}>
        <ApprovalsTutorialContent {...actions} isSaving={isSaving} setIsSaving={setIsSaving} />
      </Layout.Column>
    </Modal>
  );
};
